import { formatISODate, parseISODateString } from 'shared-utils/date-utils'
import { eachDayOfInterval, getISODay } from 'date-fns'
import type { TDateRangeMap, ISelectionOptions } from 'shared-types/calendar'

export type { TDateRangeMap, ISelectionState, ISelectionOptions } from 'shared-types/calendar'

/**
 * Convert selected string array to Date array
 */
export function convertSelectedToDates(selected: string | string[]): Date[] {
  if (typeof selected === 'string') {
    selected = selected.split(',')
  }
  if (selected.length === 1 && selected[0] === '') {
    return []
  }
  return selected.map((d: string) => parseISODateString(d))
}

/**
 * Update the range map for visualizing date ranges
 */
export function updateRangeMap(start: Date, end: Date): TDateRangeMap {
  const days = eachDayOfInterval({ start, end })
  const inRange: TDateRangeMap = {}

  if (Array.isArray(days) && days.length) {
    for (let i = 0; i < days.length; i++) {
      const day = days[i]
      const isInRange = day > start && day < end
      inRange[formatISODate(day)] = isInRange
    }
  }

  return inRange
}

/**
 * Check if a range selection is allowed (no excluded dates in between)
 */
export function isRangeAllowed(
  date: Date,
  selectedDates: Date[],
  excludedates: Date[],
  excludeweekdays: string[],
): boolean {
  if (selectedDates.length !== 1) return true

  const days = eachDayOfInterval({
    start: selectedDates[0],
    end: date,
  })

  if (!Array.isArray(days) || !days.length) return true

  for (let i = 0; i < days.length; i++) {
    // Check excluded dates
    for (const excludedDate of excludedates ?? []) {
      if (excludedDate > selectedDates[0] && excludedDate < date) {
        return false
      }
    }

    // Check excluded weekdays
    if ((excludeweekdays ?? []).includes(getISODay(days[i]).toString())) {
      return false
    }
  }

  return true
}

/**
 * Add a date to the selection
 */
export function addToSelection(
  selectedDate: Date,
  currentSelected: string[],
): string[] {
  const dateISO = formatISODate(selectedDate)
  if (currentSelected.includes(dateISO)) {
    return currentSelected
  }
  return [...currentSelected, dateISO]
}

/**
 * Remove a date from the selection
 */
export function removeFromSelection(
  selectedDate: Date,
  currentSelected: string[],
): string[] {
  const dateISO = formatISODate(selectedDate)
  const index = currentSelected.indexOf(dateISO)

  if (index === -1) return currentSelected
  if (currentSelected.length === 1) return []

  const newSelected = [...currentSelected]
  newSelected.splice(index, 1)
  return newSelected
}

/**
 * Toggle a date in the selection
 */
export function toggleSelection(
  selectedDate: Date,
  currentSelected: string[],
  maxMultiple: number,
): string[] {
  const dateISO = formatISODate(selectedDate)

  if (currentSelected.includes(dateISO)) {
    return removeFromSelection(selectedDate, currentSelected)
  }

  if (maxMultiple > 0 && currentSelected.length >= maxMultiple) {
    return currentSelected
  }

  return addToSelection(selectedDate, currentSelected)
}

/**
 * Handle range selection logic
 */
export function handleRangeSelection(
  selectedDate: Date,
  currentSelected: string[],
  options: Pick<ISelectionOptions, 'excludedates' | 'excludeweekdays'>,
): string[] {
  const dateISO = formatISODate(selectedDate)
  const selectedDates = convertSelectedToDates(currentSelected)

  // If clicking on already selected date
  if (currentSelected.includes(dateISO)) {
    // If it's the first date, clear selection
    if (currentSelected.indexOf(dateISO) === 0) {
      return []
    }
    // Otherwise remove it
    return removeFromSelection(selectedDate, currentSelected)
  }

  // If we have more than 1 selection, start over
  if (currentSelected.length > 1) {
    return [dateISO]
  }

  // If we have 1 selection, check if range is valid
  if (currentSelected.length === 1) {
    if (!isRangeAllowed(selectedDate, selectedDates, options.excludedates, options.excludeweekdays)) {
      return [dateISO]
    }
    if (selectedDates[0] > selectedDate) {
      return [dateISO]
    }
  }

  return addToSelection(selectedDate, currentSelected)
}
